import React, { useState, useEffect, useMemo } from "react";
import ReportModal from "./ReportModal";
import Grouping from "./Grouping";
import ClientsListHeader from "./ClientsList.header";
import ClientsListSidebar from "./ClientsList.sidebar";
import { apiFetch, hasRole } from "./api";

import {  
  page, header, container, topBar, title, clientCard, clientRow, expand, clientName, clientEmail, actions, detailsCard, detailsHeader, detailRow, primaryBtn, primaryBtnSmall, secondaryBtn 
  } from "./ClientsList.styles";

export default function ClientsList({
  account,
  onSelectClient,
  onGrouping,
  onTemplates,
  onUsers,
  onChangePassword,
  onLogout,
}) {
  const [clients, setClients] = useState([]);
  const [waybills, setWaybills] = useState([]);
  const [openClients, setOpenClients] = useState(new Set());
  const [reportClient, setReportClient] = useState(null);
  // const [selectedFilter, setSelectedFilter] = useState("Viiratsi");
  const [selectedSite, setSelectedSite] = useState(null);
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");     // YYYY-MM-DD
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());
  const [reportStatus, setReportStatus] = useState(new Map()); // client_id -> status
  const [clearing, setClearing] = useState(false);

  const canManage = hasRole(account, "manager");



useEffect(() => {
  apiFetch("/api/clients")
    .then(res => res.json())
    .then(data => {
      const mapped = data.map(c => ({
        id: c.id,
        name: c.company_name,
        reg_code: c.company_reg_code,
        email: c.company_email,
        email_cc: c.company_email_cc,
        email_text: c.email_text,
        grupp: c.grupp,
        report_frequency: c.report_frequency
      }));
      setClients(mapped);
      setSelectedClientIds(new Set(mapped.map(x => x.id))); // ✅ default: all visible
    })
    .catch(err => console.error("Failed to load clients", err));
}, []);

// API kaudu võtab õigest tabelist veoselehed ja mapib need setWaybillsi
useEffect(() => {
  if (!selectedSite) {
    setWaybills([]);
    return;
  }

  apiFetch(`/api/waybills?site=${selectedSite.toLowerCase()}`)
    .then((res) => res.json())

    .then((data) => {
      const mapped = data.map(w => ({
        id: w.id,
        from: w.from,
        to: w.to,
        sortiment: w.sortiment,
        amount: w.amount,
        time: w.created_timestamp,
        dateISO: toISODate(w.created_timestamp),
        date: formatDate(new Date(w.created_timestamp)),
        sender_id: w.sender_id,
        receiver_id: w.receiver_id
      }));
      setWaybills(mapped);
    })
    
    .catch((err) => console.error("Failed to load clients", err));
}, [selectedSite]);

// Report-sent tracking for the selected site.
useEffect(() => {
  if (!selectedSite) {
    setReportStatus(new Map());
    return;
  }
  loadReportStatus(selectedSite);
}, [selectedSite]);

function loadReportStatus(site) {
  apiFetch(`/api/report-status?site=${site.toLowerCase()}`)
    .then((res) => (res.ok ? res.json() : []))
    .then((data) => {
      const map = new Map();
      (Array.isArray(data) ? data : []).forEach((s) => map.set(s.client_id, s));
      setReportStatus(map);
    })
    .catch((err) => console.error("Failed to load report status", err));
}

// Manual green square (manager+).
async function toggleMarked(clientId, next) {
  // Optimistic — revert on failure.
  setReportStatus((prev) => {
    const m = new Map(prev);
    m.set(clientId, { ...(m.get(clientId) || {}), client_id: clientId, marked_done: next });
    return m;
  });

  try {
    const res = await apiFetch(`/api/report-status/${clientId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site: selectedSite.toLowerCase(), marked_done: next }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch (err) {
    console.error("Failed to set report status", err);
    loadReportStatus(selectedSite);
  }
}

async function clearAllMarked() {
  if (
    !window.confirm(
      `Tühjenda kõik märgistused objektil ${selectedSite}?\n\nKuupäevad jäävad alles.`
    )
  ) {
    return;
  }
  setClearing(true);
  try {
    const res = await apiFetch("/api/report-status/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site: selectedSite.toLowerCase() }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    loadReportStatus(selectedSite);
  } catch (err) {
    console.error("Failed to clear report status", err);
  } finally {
    setClearing(false);
  }
}

  

// id -> client, and a client's group (parent), resolved live from clients.grupp
// (own id = standalone, another id = child of that parent).
const clientsById = useMemo(() => {
  const m = new Map();
  clients.forEach((c) => m.set(c.id, c));
  return m;
}, [clients]);

function groupOf(clientId) {
  const c = clientsById.get(clientId);
  return c ? c.grupp : clientId;
}

// Group (parent) ids that have a waybill in the selected site + date range.
const groupIdsWithWaybills = useMemo(() => {
  const ids = new Set();
  for (const w of waybills) {
    if (!rowMatchesDate(w)) continue;
    if (w.sender_id != null) ids.add(groupOf(w.sender_id));
    if (w.receiver_id != null) ids.add(groupOf(w.receiver_id));
  }
  return ids;
}, [waybills, startDate, endDate, clientsById]);

// Rows are only parent/standalone companies (grupp == id) that have group
// waybills for the site; children roll up into their parent.
const clientsForSite = useMemo(() => {
  if (!selectedSite) return [];
  return clients.filter((c) => c.grupp === c.id && groupIdsWithWaybills.has(c.id));
}, [clients, groupIdsWithWaybills, selectedSite]);

const displayedClients = useMemo(() => {
  return clientsForSite.filter((c) => selectedClientIds.has(c.id));
}, [clientsForSite, selectedClientIds]);
  
function toggleClientTick(id) {setSelectedClientIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
}

function selectAllClients() {
  setSelectedClientIds(new Set(clients.map((c) => c.id)));
}

function clearAllClients() {
  setSelectedClientIds(new Set());
}

function toISODate(ts) {
  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function rowMatchesDate(row) {
  // ISO compare works for YYYY-MM-DD
  if (startDate && row.dateISO && row.dateISO < startDate) return false;
  if (endDate && row.dateISO && row.dateISO > endDate) return false;
  return true;
}

    
function toggle(client) {
  const next = new Set(openClients);

  if (next.has(client.id)) {
    next.delete(client.id);
  } else {
    next.add(client.id);
  }

  setOpenClients(next);
}



  
  
  // Yellow once a report is "due": 25 days for monthly clients, 12 for
  // semimonthly ones. Never sent also counts as due.
  function isReportDue(client, lastSentAt) {
    if (!lastSentAt) return true;
    const limit = client.report_frequency === "semimonthly" ? 12 : 25;
    const days = (Date.now() - new Date(lastSentAt).getTime()) / 86400000;
    return days >= limit;
  }

  function formatDate(d) {
    return `${String(d.getDate()).padStart(2, "0")}.${String(
      d.getMonth() + 1
    ).padStart(2, "0")}.${d.getFullYear()}`;
  }

  function includesFilter(text, filter) {
    if (!text || !filter) return false;
    return String(text).toLowerCase().includes(String(filter).toLowerCase());
  }


  return (
    <div style={page}>
      {/* ✅ Header moved to its own file */}
      <ClientsListHeader
        selected={selectedSite}
        onSelect={setSelectedSite}
        onLogout={onLogout}
        onGrouping={onGrouping}
        onTemplates={onTemplates}
        onUsers={onUsers}
        onChangePassword={onChangePassword}
        account={account}
      />


      {/* Sidebar + Main */}
      <div style={layoutStyle}>
        {/* LEFT SIDEBAR */}
        <ClientsListSidebar
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          clients={clientsForSite}
          selectedClientIds={selectedClientIds}
          onToggleClientTick={toggleClientTick}
          onSelectAll={selectAllClients}
          onClearAll={clearAllClients}
          emptyText={
            selectedSite
              ? "Selle objekti jaoks veoseid pole"
              : "Vali objekt"
          }
        />
      
        {/* MAIN CONTENT */}
        <main style={mainStyle}>
          <div style={container}>
            <div style={topBar}>
              <h2 style={title}>Tarnijad</h2>

              {canManage && selectedSite && (
                <button
                  type="button"
                  onClick={clearAllMarked}
                  disabled={clearing}
                  style={clearing ? clearBtnBusy : clearBtn}
                  title="Tühjenda kõik rohelised märgistused sellel objektil"
                >
                  {clearing ? "Tühjendan…" : "Tühjenda märgistused"}
                </button>
              )}
            </div>


        {/* SIIT */}

              {!selectedSite && (
                <div style={siteHint}>
                  Vali üleval objekt, et näha kliente ja veoseid.
                </div>
              )}

              {displayedClients.map((c) => {
                const clientWaybills = waybills
                  .filter(
                    (w) =>
                      (groupOf(w.sender_id) === c.id || groupOf(w.receiver_id) === c.id) &&
                      rowMatchesDate(w)
                  )
                  .sort((a, b) => new Date(b.time) - new Date(a.time)); // newest first

                const status = reportStatus.get(c.id);
                const marked = !!status?.marked_done;

                return (
                  <div key={c.id} style={clientCard}>
                    <div style={clientRow}>
                      <div onClick={() => toggle(c)} style={expand}>
                        {openClients.has(c.id) ? "−" : "+"}
                      </div>
              
                      <div style={{ flex: 1 }}>
                        <div style={clientName}>{c.name}</div>
                        <div style={clientEmail}>{c.email}</div>
                      </div>
              
                      <div style={actions}>
                        {/* Last sent — automatic, yellow when a report is due */}
                        <span
                          style={
                            isReportDue(c, status?.last_sent_at) ? sentDateDue : sentDateOk
                          }
                          title={
                            status?.last_sent_at
                              ? `Viimane raport saadetud ${formatDate(
                                  new Date(status.last_sent_at)
                                )}`
                              : "Raportit pole veel saadetud"
                          }
                        >
                          {status?.last_sent_at
                            ? formatDate(new Date(status.last_sent_at))
                            : "—"}
                        </span>

                        {/* Manual square — manager+ only */}
                        <button
                          type="button"
                          onClick={() => canManage && toggleMarked(c.id, !marked)}
                          disabled={!canManage}
                          style={{
                            ...markBox,
                            background: marked ? "#4CAF50" : "#FFFFFF",
                            borderColor: marked ? "#3d9140" : "#C7D3EE",
                            cursor: canManage ? "pointer" : "default",
                          }}
                          title={
                            canManage
                              ? marked
                                ? "Märgi tegemata"
                                : "Märgi tehtuks"
                              : "Märkimiseks puuduvad õigused"
                          }
                        />

                        <button style={secondaryBtn} onClick={() => onSelectClient(c)}>
                          Seaded
                        </button>
                        <button
                          style={primaryBtnSmall}
                          onClick={() => setReportClient(c)}
                        >
                          Raport
                        </button>
                      </div>
                    </div>
              
                    {openClients.has(c.id) && (
                      <div style={detailsCard}>
                        {/* Middle layer: per-assortment summary, broken down by route */}
                        <WaybillSummary rows={clientWaybills} />

                        {/* Detailed waybills (own scrollable frame, ~10 rows) */}
                        <div style={detailSectionTitle}>Veoselehed</div>
                        <div style={detailScroll}>
                          <div style={detailsHeader}>
                            <span>Number</span>
                            <span>Lähtekoht</span>
                            <span>Sihtkoht</span>
                            <span>Sortiment</span>
                            <span>Kogus</span>
                            <span>Kuupäev</span>
                          </div>

                          {clientWaybills.map((r) => (
                            <div key={r.id} style={detailRow}>
                              <span
                                style={waybillNumberCell}
                                title="Ava Waybilleris"
                                onClick={() => openWaybiller(r.id)}
                              >
                                {r.id}
                              </span>
                              <span>{r.from}</span>
                              <span>{r.to}</span>
                              <span>{r.sortiment}</span>
                              <span>{r.amount}</span>
                              <span>{r.date}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}


                        {/* SIIANI */}
            </div>
          </main>
        </div>

      {reportClient && (
        <ReportModal
          client={reportClient}
          site={selectedSite}
          clients={clients}
          initialStartDate={startDate}
          initialEndDate={endDate}
          onSent={() => selectedSite && loadReportStatus(selectedSite)}
          onClose={() => setReportClient(null)}
        />
      )}
    </div>
  );
}


// Build a summary: per assortment (sortiment) total, broken down by route (from → to).
function buildWaybillSummary(rows) {
  const byAssortment = new Map();

  for (const r of rows) {
    const sortiment = r.sortiment || "—";
    const amount = Number(r.amount) || 0;

    if (!byAssortment.has(sortiment)) {
      byAssortment.set(sortiment, { sortiment, total: 0, routes: new Map() });
    }
    const a = byAssortment.get(sortiment);
    a.total += amount;

    const from = r.from || "—";
    const to = r.to || "—";
    const key = `${from}→${to}`;
    if (!a.routes.has(key)) {
      a.routes.set(key, { from, to, amount: 0, count: 0 });
    }
    const route = a.routes.get(key);
    route.amount += amount;
    route.count += 1;
  }

  return Array.from(byAssortment.values())
    .map((a) => ({
      ...a,
      routes: Array.from(a.routes.values()).sort((x, y) => y.amount - x.amount),
    }))
    .sort((x, y) => y.total - x.total);
}

function fmtAmount(n) {
  return Number(n || 0).toLocaleString("et-EE", { maximumFractionDigits: 3 });
}

// "BPO2442106" -> opens https://app.waybiller.com/waybills/finished/2442106 in a new tab.
function openWaybiller(wbId) {
  const num = String(wbId || "").replace(/\D/g, "");
  if (!num) return;
  const url = `https://app.waybiller.com/waybills/finished/${num}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function WaybillSummary({ rows }) {
  const summary = buildWaybillSummary(rows);
  const [collapsed, setCollapsed] = useState(() => new Set());

  if (summary.length === 0) return null;

  const assortmentKeys = summary.map((a) => a.sortiment);
  const allCollapsed = assortmentKeys.every((k) => collapsed.has(k));

  function toggleAssortment(key) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function toggleAll() {
    setCollapsed(allCollapsed ? new Set() : new Set(assortmentKeys));
  }

  return (
    <div style={summaryWrap}>
      <div style={summaryHeader}>
        <span style={summaryTitle}>Kokkuvõte</span>
        <button type="button" style={summaryToggleAllBtn} onClick={toggleAll}>
          {allCollapsed ? "Ava kõik" : "Sulge kõik"}
        </button>
      </div>

      {summary.map((a) => {
        const isOpen = !collapsed.has(a.sortiment);
        return (
          <div key={a.sortiment} style={summaryAssortment}>
            <div
              style={{
                ...summaryAssortmentHead,
                ...(isOpen
                  ? {}
                  : { borderBottom: "none", marginBottom: 0, paddingBottom: 0 }),
              }}
              onClick={() => toggleAssortment(a.sortiment)}
              role="button"
            >
              <span style={summaryAssortmentName}>
                <span style={summaryChevron}>{isOpen ? "▾" : "▸"}</span>
                {a.sortiment}
              </span>
              <span style={summaryAssortmentTotal}>{fmtAmount(a.total)}</span>
            </div>

            {isOpen &&
              a.routes.map((route) => (
                <div key={`${route.from}→${route.to}`} style={summaryRoute}>
                  <span style={summaryRouteName}>
                    {route.from} → {route.to}
                  </span>
                  <span style={summaryRouteAmount}>{fmtAmount(route.amount)}</span>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}

const summaryWrap = {
  marginBottom: "16px",
};

const summaryHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px",
};

const summaryTitle = {
  fontWeight: 700,
  color: "#0D1F4D",
  fontSize: "15px",
};

const summaryToggleAllBtn = {
  background: "#F3F6FD",
  border: "1px solid #E6ECF8",
  borderRadius: "8px",
  padding: "4px 10px",
  cursor: "pointer",
  fontWeight: 700,
  color: "#0D1F4D",
  fontSize: "12px",
};

const summaryChevron = {
  display: "inline-block",
  width: "14px",
  marginRight: "4px",
  color: "#8CA5E6",
  fontSize: "11px",
};

const summaryAssortment = {
  background: "white",
  border: "1px solid #E6ECF8",
  borderRadius: "10px",
  padding: "10px 12px",
  marginBottom: "8px",
};

const summaryAssortmentHead = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontWeight: 700,
  color: "#0D1F4D",
  fontSize: "14px",
  paddingBottom: "6px",
  borderBottom: "1px solid #E6ECF8",
  marginBottom: "6px",
  cursor: "pointer",
  userSelect: "none",
};

const summaryAssortmentName = {
  fontWeight: 700,
};

const summaryAssortmentTotal = {
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
};

const summaryRoute = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "13px",
  color: "#41527A",
  padding: "3px 0 3px 14px",
};

const summaryRouteName = {
  paddingRight: "12px",
};

const summaryRouteAmount = {
  fontVariantNumeric: "tabular-nums",
  color: "#0D1F4D",
  whiteSpace: "nowrap",
};

const detailSectionTitle = {
  fontWeight: 700,
  color: "#0D1F4D",
  margin: "4px 0 8px",
  fontSize: "15px",
};

const detailScroll = {
  maxHeight: "360px",
  overflowY: "auto",
  background: "white",
  border: "1px solid #E6ECF8",
  borderRadius: "10px",
  padding: "0 12px 8px",
};

// Keep the number's look unchanged — only signal it's clickable.
const waybillNumberCell = {
  cursor: "pointer",
};

// Last-sent date badge: neutral when recent, yellow once a report is due.
const sentDateBase = {
  fontSize: "12px",
  fontWeight: 700,
  borderRadius: "8px",
  padding: "5px 10px",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

const sentDateOk = {
  ...sentDateBase,
  background: "#F3F6FD",
  border: "1px solid #E6ECF8",
  color: "#6C7A9A",
};

const sentDateDue = {
  ...sentDateBase,
  background: "#FFF4CC",
  border: "1px solid #F0D488",
  color: "#8a6d1f",
};

// Manual "handled" square.
const markBox = {
  width: "26px",
  height: "26px",
  borderRadius: "6px",
  border: "2px solid #C7D3EE",
  padding: 0,
  flexShrink: 0,
};

const clearBtn = {
  background: "#F3F6FD",
  border: "1px solid #8CA5E6",
  borderRadius: "999px",
  padding: "6px 14px",
  cursor: "pointer",
  fontWeight: 700,
  color: "#8CA5E6",
  fontSize: "12px",
};

const clearBtnBusy = {
  ...clearBtn,
  color: "#6C7A9A",
  borderColor: "#E6ECF8",
  cursor: "not-allowed",
};

const siteHint = {
  background: "white",
  borderRadius: "14px",
  padding: "28px",
  textAlign: "center",
  color: "#555",
  fontSize: "15px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
};

const layoutStyle = {
  display: "flex",
  gap: "28px",
  width: "1200px",
  maxWidth: "100%",
  margin: "0 auto",
  padding: "0 16px",
  boxSizing: "border-box",
  alignItems: "stretch",
  flex: 1, // fill the height below the header
  minHeight: 0, // let the columns scroll instead of the page
  overflow: "hidden",
};

const mainStyle = {
  flex: 1,
  minWidth: 0,
  overflowY: "auto", // clients list scrolls independently
  overflowX: "hidden",
};
